import { createContext, useContext, useState, useEffect, useCallback } from "react";
import {
  loginApi,
  getUsers,
  editUser,
  createUser,
  deleteUser,
  getChatRooms,
} from "../api/api";
import { logActivity } from "../api/api";
import { normalizeRole, ADMIN_ROLES, hasAnyRole } from "../utils/roles";
// import toast from "react-hot-toast";
import {
  isGroupRoom,
  clearRoomRegistry,
  registerGroupRoom,
  setCompanyRoomId,
} from "../utils/roomRegistry";
import {
  connectSocket,
  getSocket,
  emitWhenConnected,
  subscribeToNotifications,
  getCurrentRoomId,
  joinRoom,
} from "../utils/socket";
import toast from "react-hot-toast";

const AuthContext = createContext();

const normalizeUserRole = (user) =>
  user ? { ...user, role: normalizeRole(user.role) || user.role } : user;

const UNREAD_STORAGE_KEY = "unreadCounts";

const readUnreadFromStorage = () => {
  try {
    const raw = localStorage.getItem(UNREAD_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeUnreadToStorage = (counts) => {
  try {
    localStorage.setItem(UNREAD_STORAGE_KEY, JSON.stringify(counts));
  } catch (err) {
    console.warn("Failed to persist unread counts:", err);
  }
};

const decodeToken = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

const isTokenExpired = (token) => {
  try {
    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp) return true;
    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp < currentTime;
  } catch {
    return true;
  }
};

const getId = (u) => u?._id || u?.id;

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const stored = localStorage.getItem("user");
      if (!stored || stored === "undefined") return null;
      return normalizeUserRole(JSON.parse(stored));
    } catch {
      return null;
    }
  });

  // ✅ يقرأ التوكن بكلا المفتاحين عشان يتوافق مع socket.js
  const [token, setToken] = useState(
    () => localStorage.getItem("Token") || localStorage.getItem("token") || null
  );

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [allUsersForChat, setAllUsersForChat] = useState([]);

  const [unreadCounts, setUnreadCounts] = useState(() => readUnreadFromStorage());

  useEffect(() => {
    writeUnreadToStorage(unreadCounts);
  }, [unreadCounts]);

  const totalUnreadCount = Object.values(unreadCounts).reduce(
    (sum, n) => sum + (Number(n) || 0),
    0
  );

  const incrementUnread = useCallback((conversationKey) => {
    if (!conversationKey) return;
    setUnreadCounts((prev) => ({
      ...prev,
      [conversationKey]: (Number(prev[conversationKey]) || 0) + 1,
    }));
  }, []);

  const markRoomRead = useCallback((conversationKey) => {
    if (!conversationKey) return;
    setUnreadCounts((prev) => {
      if (!prev[conversationKey]) return prev;
      const next = { ...prev };
      delete next[conversationKey];
      return next;
    });
  }, []);

  const syncUser = (user) => {
    const normalizedUser = normalizeUserRole(user);
    setCurrentUser(normalizedUser);
    localStorage.setItem("user", JSON.stringify(normalizedUser));

    setUsers((prev) => {
      const uid = normalizedUser?._id || normalizedUser?.id;
      if (!uid) return prev;

      const list = Array.isArray(prev) ? prev : [];
      let found = false;

      const next = list.map((u) => {
        const id = u?._id || u?.id;
        if (String(id) === String(uid)) {
          found = true;
          return normalizeUserRole({
            ...u,
            ...normalizedUser,
            profilePicture: normalizedUser?.profilePicture?.url
              ? normalizedUser.profilePicture
              : { ...u?.profilePicture, ...normalizedUser?.profilePicture },
            photo: normalizedUser?.photo || u?.photo,
          });
        }
        return u;
      });

      return found ? next : [normalizedUser, ...next];
    });

    window.dispatchEvent(new Event("userChanged"));
  };

  const login = async (email, password) => {
    const res = await loginApi({ email, password });

    if (!res) {
      throw new Error("No response from server");
    }

    const accessToken =
      res.accessToken ||
      res.token ||
      res.access_token;

    if (!accessToken) {
      throw new Error(
        res.message ||
        res.error ||
        "Invalid email or password"
      );
    }

    // ✅ احفظ التوكن بالمفتاحين عشان socket.js و AuthContext يشتغلوا صح
    localStorage.setItem("token", accessToken);
    localStorage.setItem("Token", accessToken);
    setToken(accessToken);

    const decoded = decodeToken(accessToken);

    const user = normalizeUserRole(
      res.user ||
      res.data || {
        ...decoded,
        email,
      }
    );

    syncUser(user);

    return res;
  };

  const refreshCurrentUser = async () => {
    try {
      const res = await getUsers();
      const list = Array.isArray(res) ? res : res.data || [];

      const uid = currentUser?._id || currentUser?.id;

      const updatedUser = list.find(
        (u) => String(u._id || u.id) === String(uid)
      );

      if (updatedUser) syncUser(updatedUser);
    } catch (err) {
      console.error("Refresh user error:", err);
    }
  };

  const fetchUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      const res = await getUsers();
      const usersData = (
        Array.isArray(res) ? res :
        Array.isArray(res.data) ? res.data :
        Array.isArray(res.data?.data) ? res.data.data :
        []
      ).map(normalizeUserRole);

      setUsers(usersData);
    } catch (err) {
      console.error("Fetch users error:", err);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const addUser = async (data) => {
    const res = await createUser(data);
    await fetchUsers();
    return res;
  };

  const updateUser = async (id, data) => {
    const res = await editUser(id, data);
    setUsers((prev) =>
      prev.map((u) =>
        String(u._id || u.id) === String(id)
          ? normalizeUserRole({ ...u, ...data })
          : u
      )
    );

    fetchUsers();

    if (String(currentUser?._id || currentUser?.id) === String(id)) {
      await refreshCurrentUser();
    }

    return res;
  };

  const removeUser = async (id) => {
    const res = await deleteUser(id);

    setUsers((prev) =>
      prev.filter((u) => String(u._id || u.id) !== String(id))
    );

    return res;
  };

  const logout = useCallback(async () => {
    try {
      await logActivity("offline");
    } catch (err) {
      console.warn("Log offline failed:", err);
    }

    setCurrentUser(null);
    setUsers([]);
    setToken(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    // ✅ امسح كلا المفتاحين عند الـ logout
    localStorage.removeItem("Token");
    setUnreadCounts({});
    localStorage.removeItem(UNREAD_STORAGE_KEY);

    // ✅ FIX: امسح الـ session marker بتاع الـ status هنا كمان. من غير
    // السطر ده، لو نفس اليوزر عمل login تاني في نفس التاب (من غير ما
    // يقفل المتصفح)، StatusContext كان بيلاقي الـ marker لسه متسجل
    // لنفس الـ userId ويفترض غلط إن الجلسة دي "اتعمللها reset بالفعل"،
    // فيسيب الـ status القديم (اللي ممكن يكون كان "active" قبل
    // الـ logout) زي ما هو في localStorage، فيظهر "Active" في الـ
    // Topbar وهو فعليًا offline على السيرفر — وده بالظبط اللي كان
    // بيحصل (لاحظنا الحالة دي في اللوج).
    sessionStorage.removeItem("status-session-marker");
    clearRoomRegistry();
    window.dispatchEvent(new Event("userChanged"));
  }, []);

  useEffect(() => {
    if (currentUser && hasAnyRole(currentUser, ADMIN_ROLES)) {
      fetchUsers();
    }
  }, [currentUser?._id, currentUser?.role, fetchUsers]);

  useEffect(() => {
    const checkExpiration = () => {
      if (window.location.pathname === "/login") return;

      // ✅ تحقق من كلا المفتاحين
      const currentToken = localStorage.getItem("Token") || localStorage.getItem("token");

      if (currentToken && isTokenExpired(currentToken)) {
        logout();
        window.location.href = "/login";
      }
    };

    if (window.location.pathname !== "/login") {
      checkExpiration();
    }

    const interval = setInterval(checkExpiration, 60 * 1000);

    return () => clearInterval(interval);
  }, [logout]);

  // ─── تحميل يوزرز الشات (لأسماء المرسلين في التوست) ─────────────
  useEffect(() => {
    if (!token || !currentUser) return;

    const BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.growthstationco.com";
    // ✅ استخدم كلا المفتاحين
    const authToken = token || localStorage.getItem("Token") || localStorage.getItem("token");

    fetch(`${BASE_URL}/employee/`, {
      headers: { Authorization: authToken, "Content-Type": "application/json" },
    })
      .then((r) => r.json())
      .then((json) => {
        const list = Array.isArray(json) ? json : json?.data ?? json?.users ?? [];
        const usersList = list
          .filter((u) => getId(u) !== getId(currentUser))
          .map((u) => ({
            ...u,
            avatar: u.avatar || u.profilePicture?.url || u.profilePicture || null,
            userName: u.userName || u.name || "Unknown",
          }));

        setAllUsersForChat(usersList);
      })
      .catch((err) => console.error("Failed to load users for chat:", err));
  }, [token, currentUser]);

  // ✅ FIX (الأهم): الانضمام لكل الـ rooms الحقيقية (private + group)
  // بيحصل دلوقتي من هنا — من AuthProvider نفسه اللي لافّ التطبيق
  // كله — بدل ما يعتمد على فتح صفحة Messages. قبل كده كان بيتعمل
  // join لاسم room "مصنّع" محليًا (private_userId1_userId2) مش
  // الـ roomId الحقيقي اللي الباك إند بيستخدمه، فالسوكيت مكنش
  // فعليًا عضو في أي غرفة حقيقية إلا لما تفتح Messages.jsx وتعمل
  // fetch/create للـ room بالـ id الصح. النتيجة كانت: الإشعارات
  // بتوصل بس وانت داخل صفحة الرسايل (أو بعد ما تكون فتحتها قبل كده
  // في نفس الـ session وعمل joinRoom بالـ id الصح).
  //
  // دلوقتي بنجيب كل الـ rooms الحقيقية بتاعتك (GET /chat/rooms) أول
  // ما تسجل دخول / تفتح التطبيق، ونعمل لها joinRoom بالـ _id الصح،
  // وبنسجل الجروبات في roomRegistry (عشان isGroupRoom/getCompanyRoomId
  // يشتغلوا صح في أي مكان بالتطبيق). طالما الـ joinRoom اتعمل مرة
  // واحدة بالـ id الصح، socket.js بيحفظه في joinedRoomIds وبيعيد
  // الانضمام له أوتوماتيك عند أي reconnect — مش محتاجين نكرر ده.
  //
  // ⚠ ملحوظة: ده بيغطي المحادثات (خاص أو جروب) اللي already موجودة.
  // لو حصل أول رسالة على الإطلاق في محادثة خاصة جديدة تمامًا (لسه
  // مالهاش room في الداتابيز)، الطرف التاني هيعمل joinRoom بعد ما
  // الباك إند ينشئ الـ room، لكن انت (المستقبل) لسه معندكش الـ id ده
  // في الـ Set بتاعتك لحد ما تفتح Messages أو تعمل refresh — ده حد
  // بتاع الفرونت إند وحده، محتاج تعديل في الباك إند (مثلاً يبعت
  // event على غرفة شخصية باسم userId) لو عايز تغطيه كمان.
  useEffect(() => {
    if (!token || !currentUser) return undefined;

    let cancelled = false;
    const authToken = token || localStorage.getItem("Token") || localStorage.getItem("token");

    const joinAllMyRooms = async () => {
      try {
        const rooms = await getChatRooms();
        if (cancelled) return;

        const list = Array.isArray(rooms) ? rooms : (rooms?.data ?? []);
        if (!list.length) return;

        const doJoin = () => {
          list.forEach((room) => {
            const roomId = room?._id;
            if (!roomId) return;

            joinRoom(roomId, authToken);

            if (room.type === "GROUP") {
              registerGroupRoom(roomId);
              if (room.isCompanyWide) setCompanyRoomId(roomId);
            }
          });
          console.log("🚪 [AuthContext] Auto-joined all rooms (private + group):", list.length);
        };

        const socket = connectSocket(authToken);
        if (!socket) return;

        if (socket.connected) {
          doJoin();
        } else {
          socket.once("connect", doJoin);
        }
      } catch (err) {
        console.error("Failed to auto-join chat rooms:", err);
      }
    };

    joinAllMyRooms();

    return () => { cancelled = true; };
  }, [token, currentUser?._id]);

  useEffect(() => {
    if (!currentUser || !token) return undefined;

    const socket = connectSocket(token);
    if (!socket) return undefined;

    const myId = getId(currentUser);

    const unsubscribe = subscribeToNotifications((msg) => {
      const senderId =
        typeof msg.senderId === "object" ? getId(msg.senderId) : msg.senderId;

      if (senderId === myId) return;

      const roomId = msg.roomId;
      const isViewingThisRoom =
        roomId != null && String(roomId) === String(getCurrentRoomId());

      if (!isViewingThisRoom) {
        const conversationKey = isGroupRoom(roomId) ? roomId : senderId;
        incrementUnread(conversationKey);

        const senderInfo = typeof msg.senderId === "object" ? msg.senderId : null;
        const senderName = senderInfo?.userName || senderInfo?.name ||
                           allUsersForChat.find((u) => getId(u) === senderId)?.userName ||
                           "Unknown";

        toast.success(`رسالة جديدة من ${senderName}`, {
          duration: 4000,
          icon: "💬",
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [currentUser, token, allUsersForChat, incrementUnread]);

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        users,
        loadingUsers,
        token,
        login,
        logout,
        fetchUsers,
        addUser,
        updateUser,
        refreshCurrentUser,
        removeUser,
        syncUser,
        unreadCounts,
        totalUnreadCount,
        markRoomRead,
        incrementUnread,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export default AuthContext;